import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';

const initialFormData = {
    visitanteFrecuenteId:     0,
    dniConductor:             '',
    nombreVisitante:          '',
    patente:                  '',
    tipoVehiculoId:           null,
    aseguradora:              '',
    nroPoliza:                '',
    fechaPoliza:              null,
    fechaVencimientoSeguro:   null,
    registro:                 '',
    fechaVencimientoRegistro: null,
};

const vencidoBadge = (fecha) => {
    if (!fecha) return null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    if (new Date(fecha) >= hoy) return null;
    return (
        <span style={{
            background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca',
            fontWeight: 600, fontSize: '0.7rem', padding: '0.1rem 0.5rem', borderRadius: 99, marginLeft: 6,
        }}>
            Vencido
        </span>
    );
};

const vencimientoCell = (fecha) => fecha
    ? <>{new Date(fecha).toLocaleDateString('es-AR')}{vencidoBadge(fecha)}</>
    : <span style={{ color: '#aaa' }}>—</span>;

const VisitantesFrecuentes = () => {
    const toast    = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [formData,      setFormData]      = useState(initialFormData);
    const [visible,       setVisible]       = useState(false);
    const [loading,       setLoading]       = useState(false);
    const [saving,        setSaving]        = useState(false);
    const [visitantes,    setVisitantes]    = useState([]);
    const [tiposVehiculo, setTiposVehiculo] = useState([]);

    const fetchVisitantes = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/visitanteFrecuente/GetVisitantesFrecuentes`, {
                params: { administracionId, barrioId: barrio.id },
            });
            setVisitantes(data || []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los visitantes frecuentes', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, administracionId]);

    useEffect(() => { fetchVisitantes(); }, [fetchVisitantes]);

    useEffect(() => {
        axios.get(`${HOST_API}api/tipovehiculo/GetTipoVehiculos`).then(({ data }) => setTiposVehiculo(data)).catch(() => {});
    }, [HOST_API]);

    const handleChange = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const openNew = () => { setFormData(initialFormData); setVisible(true); };

    const handleEdit = (row) => {
        setFormData({
            visitanteFrecuenteId:     row.visitanteFrecuenteId,
            dniConductor:             row.dniConductor || '',
            nombreVisitante:          row.nombreVisitante || '',
            patente:                  row.patente || '',
            tipoVehiculoId:           row.tipoVehiculoId || null,
            aseguradora:              row.aseguradora || '',
            nroPoliza:                row.nroPoliza || '',
            fechaPoliza:              row.fechaPoliza ? new Date(row.fechaPoliza) : null,
            fechaVencimientoSeguro:   row.fechaVencimientoSeguro ? new Date(row.fechaVencimientoSeguro) : null,
            registro:                 row.registro || '',
            fechaVencimientoRegistro: row.fechaVencimientoRegistro ? new Date(row.fechaVencimientoRegistro) : null,
        });
        setVisible(true);
    };

    const handleDelete = (row) => {
        confirmDialog({
            message: `¿Eliminar la ficha de "${row.nombreVisitante || row.dniConductor}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/visitanteFrecuente/DeleteVisitanteFrecuente`, {
                        params: { visitanteFrecuenteId: row.visitanteFrecuenteId },
                    });
                    fetchVisitantes();
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Visitante frecuente eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
                }
            },
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.dniConductor.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'El DNI del conductor es obligatorio', life: 3000 });
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${HOST_API}api/visitanteFrecuente/SaveVisitanteFrecuente`, {
                ...formData,
                administracionId,
                barrioId: barrio.id,
            });
            setVisible(false);
            fetchVisitantes();
            toast.current.show({ severity: 'success', summary: formData.visitanteFrecuenteId ? 'Actualizado' : 'Creado', detail: 'Visitante frecuente guardado correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el visitante frecuente', life: 3000 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <h4>Visitantes Frecuentes</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nuevo visitante" icon="pi pi-plus" onClick={openNew} />
            </div>

            {/* ─── Modal ─────────────────────────────────────────────────────────── */}
            <Dialog
                header={formData.visitanteFrecuenteId ? 'Editar visitante frecuente' : 'Nuevo visitante frecuente'}
                visible={visible}
                style={{ width: '640px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-wrap gap-3 mt-2">

                        <div className="w-full md:w-5">
                            <label style={lbl}>DNI del conductor <span style={req}>*</span></label>
                            <InputText
                                value={formData.dniConductor}
                                onChange={(e) => handleChange('dniConductor', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={20}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Nombre</label>
                            <InputText
                                value={formData.nombreVisitante}
                                onChange={(e) => handleChange('nombreVisitante', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={200}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Patente</label>
                            <InputText
                                value={formData.patente}
                                onChange={(e) => handleChange('patente', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={20}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Tipo de vehículo</label>
                            <Dropdown
                                value={formData.tipoVehiculoId}
                                options={tiposVehiculo}
                                optionLabel="descripcion"
                                optionValue="tipoVehiculoId"
                                onChange={(e) => handleChange('tipoVehiculoId', e.value)}
                                placeholder="Seleccioná tipo..."
                                className="p-inputtext-sm w-full"
                                showClear
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Registro (licencia de conducir)</label>
                            <InputText
                                value={formData.registro}
                                onChange={(e) => handleChange('registro', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={50}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Vencimiento del registro</label>
                            <Calendar
                                value={formData.fechaVencimientoRegistro}
                                onChange={(e) => handleChange('fechaVencimientoRegistro', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Aseguradora</label>
                            <InputText
                                value={formData.aseguradora}
                                onChange={(e) => handleChange('aseguradora', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={150}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>N° de póliza</label>
                            <InputText
                                value={formData.nroPoliza}
                                onChange={(e) => handleChange('nroPoliza', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={50}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Fecha de póliza</label>
                            <Calendar
                                value={formData.fechaPoliza}
                                onChange={(e) => handleChange('fechaPoliza', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Vencimiento del seguro</label>
                            <Calendar
                                value={formData.fechaVencimientoSeguro}
                                onChange={(e) => handleChange('fechaVencimientoSeguro', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label={saving ? 'Guardando...' : 'Guardar'} icon="pi pi-save" type="submit" disabled={saving} />
                        </div>

                    </div>
                </form>
            </Dialog>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={visitantes}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        showGridlines
                        responsiveLayout="scroll"
                        emptyMessage="No hay visitantes frecuentes registrados"
                        className="p-datatable-gridlines bg-white"
                        sortField="nombreVisitante"
                        sortOrder={1}
                    >
                        <Column field="dniConductor"    header="DNI"         sortable style={{ width: '9rem' }} />
                        <Column field="nombreVisitante" header="Nombre"      sortable />
                        <Column field="patente"         header="Patente"     style={{ width: '8rem' }} />
                        <Column field="tipoVehiculo"    header="Vehículo"    style={{ width: '9rem' }} />
                        <Column field="aseguradora"     header="Aseguradora" />
                        <Column field="nroPoliza"       header="N° Póliza"   style={{ width: '9rem' }} />
                        <Column header="Venc. Seguro" style={{ width: '10rem' }} body={(row) => vencimientoCell(row.fechaVencimientoSeguro)} />
                        <Column header="Venc. Registro" style={{ width: '10rem' }} body={(row) => vencimientoCell(row.fechaVencimientoRegistro)} />
                        <Column header="Actualizado" style={{ width: '9rem' }} body={(row) => row.fechaActualizacion ? new Date(row.fechaActualizacion).toLocaleDateString('es-AR') : '—'} />
                        <Column
                            header="Acciones"
                            style={{ width: '8rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(row)} tooltip="Editar" />
                                    <Button icon="pi pi-trash"  rounded className="btn-delete"  onClick={() => handleDelete(row)} tooltip="Eliminar" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.3rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const req = { color: '#f04438' };

export default VisitantesFrecuentes;
